import { useEffect, useId, useRef, useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { isPortalEmail, normalizeEmail, pyStrip } from '../../lib/email'
import type { AddPortalUserDraft } from '../../lib/portalAccess'
import { PORTAL_ROLES, type PortalRole } from '../../lib/types'
import { Button } from '../ui/Button'
import { Select } from '../ui/Select'

// The prototype lists Controller first and preselects it (`#add-portal-access-role`).
const ROLE_ORDER: readonly PortalRole[] = [...PORTAL_ROLES].reverse()

/** Settings › Access's Add User modal (MS-997 D6) — the prototype's
 * `#add-portal-access-modal` (subscription-portal.html): Permission first,
 * Controller first and preselected, then Email; no Type field. Built on the
 * prototype's `.modal` geometry rather than User Limits' AddUserModal, which
 * is specific to limits.
 *
 * Opening (`+ Add user`) starts from EMPTY_ADD_DRAFT with the email focused;
 * re-opened after a refused grant, it keeps the typed email and role and
 * shows the refusal in the inline slot (`#add-portal-access-error`). The
 * email is checked as the API checks it — required, a portal email (ASCII,
 * plausible), not already on the list — and the error clears on input. Enter
 * submits, Cancel or the backdrop closes. A valid email closes the form and
 * hands it on for the confirmation (`onSubmit`). */
export function AddPortalUserModal({
  initial,
  hasGrant,
  onSubmit,
  onClose,
}: Readonly<{
  initial: AddPortalUserDraft
  /** True when the (normalised) email already holds a grant here. */
  hasGrant: (email: string) => boolean
  onSubmit: (draft: { email: string; role: PortalRole }) => void
  onClose: () => void
}>) {
  const { t } = useTranslation()
  const [email, setEmail] = useState(initial.email)
  const [role, setRole] = useState<PortalRole>(initial.role)
  const [error, setError] = useState<string | null>(initial.error)
  const inputRef = useRef<HTMLInputElement>(null)
  const titleId = useId()
  const roleId = useId()
  const emailId = useId()
  const errorId = useId()

  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  function submit(event: FormEvent) {
    event.preventDefault()
    const value = normalizeEmail(email)
    if (!value) {
      setError(t('settings.access.addForm.emailRequired'))
      return
    }
    // Before lower-casing as well as after, as the API does (A3).
    if (!isPortalEmail(pyStrip(email)) || !isPortalEmail(value)) {
      setError(t('settings.access.addForm.emailInvalid'))
      return
    }
    if (hasGrant(value)) {
      setError(t('settings.access.addForm.emailExists', { email: value }))
      return
    }
    onSubmit({ email: value, role })
  }

  return (
    <div className="fixed inset-0 flex items-center justify-center z-[1000]">
      {/* The backdrop is a sibling of the dialog (AdminConfirmModal's
          pattern), a pointer convenience hidden from assistive tech — Cancel
          is the keyboard path. */}
      <button
        type="button"
        aria-hidden="true"
        tabIndex={-1}
        data-testid="add-portal-user-backdrop"
        className="absolute inset-0 bg-black/40 cursor-default"
        onClick={onClose}
      />
      <dialog
        open
        aria-modal="true"
        aria-labelledby={titleId}
        className="relative m-0 p-0 border-0 text-inherit bg-[var(--bg-card)] rounded-xl shadow-[var(--shadow-lg)] w-[420px] max-w-[90vw] overflow-hidden"
      >
        <form onSubmit={submit} noValidate>
          <div className="px-6 pt-5 pb-4 border-b border-[var(--border-light)]">
            <h3 id={titleId} className="text-[16px] font-semibold text-[var(--text-primary)]">
              {t('settings.access.addForm.title')}
            </h3>
            <p className="text-[12px] text-[var(--text-tertiary)] mt-0.5">{t('settings.access.addForm.subtitle')}</p>
          </div>
          <div className="px-6 py-5">
            <div className="mb-5">
              <label htmlFor={roleId} className="block font-semibold text-[13px] mb-1.5 text-[var(--text-primary)]">
                {t('settings.access.addForm.permissionLabel')}
              </label>
              <Select id={roleId} variant="form" value={role} onChange={(e) => setRole(e.target.value as PortalRole)}>
                {ROLE_ORDER.map((r) => (
                  <option key={r} value={r}>
                    {t(`settings.roles.${r}`)}
                  </option>
                ))}
              </Select>
            </div>
            <div>
              <label htmlFor={emailId} className="block font-semibold text-[13px] mb-1.5 text-[var(--text-primary)]">
                {t('settings.access.addForm.emailLabel')}
              </label>
              <input
                ref={inputRef}
                id={emailId}
                type="email"
                value={email}
                placeholder={t('settings.access.addForm.emailPlaceholder')}
                aria-invalid={error ? true : undefined}
                aria-describedby={error ? errorId : undefined}
                onChange={(e) => {
                  setEmail(e.target.value)
                  setError(null)
                }}
                className="w-full px-3 py-2 border border-[var(--border)] rounded-md text-[13px] text-[var(--text-primary)] bg-[var(--bg-input)] transition-[border-color,box-shadow] focus:outline-none focus:border-[var(--border-focus)] focus:shadow-[0_0_0_3px_var(--accent-subtle)]"
              />
              {error && (
                <div id={errorId} role="alert" className="mt-[7px] text-[12px] text-[var(--danger)]">
                  {error}
                </div>
              )}
            </div>
          </div>
          <div className="px-6 py-4 border-t border-[var(--border-light)] flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={onClose}>
              {t('settings.access.addForm.cancel')}
            </Button>
            <Button type="submit" variant="primary">
              {t('settings.access.addForm.submit')}
            </Button>
          </div>
        </form>
      </dialog>
    </div>
  )
}
